import { useMutation } from "@tanstack/react-query";
import { llmService } from "../services/llmService";
import { useNewton } from "../store/NewtonContext";
import { useInvalidate } from "../../../../../hooks/useInvalidate";

/**
 * Usage:
 * const { mutate: updateName, isPending } = useUpdateSessionDisplayName({
 *   llmEnv,
 *   userId: userData?.uid,
 *   onSuccess: () => setEditingId(null),
 * })
 */
export function useUpdateSessionDisplayName({ userId, onSuccess } = {}) {
  const { llmEnv } = useNewton();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ displayName, sessionId }) => {
      const res = await llmService.updateSessionDisplayName({
        displayName,
        env: llmEnv,
        sessionId,
      });

      if (!res.ok) {
        throw new Error(`Failed to update session display name: ${res.status}`);
      }

      const responseText = await res.text();
      if (!responseText) {
        throw new Error("Empty response from update session display name");
      }

      // Parse the JSON string response as per API spec
      try {
        const parsedResponse = JSON.parse(responseText);
        return parsedResponse;
      } catch (error) {
        throw new Error("Failed to parse update session display name response");
      }
    },
    onSuccess: async (...args) => {
      try {
        if (typeof onSuccess === "function") onSuccess(...args);

        if (userId && llmEnv) {
          invalidate(["getLlmSessions_key"]);
        }
      } catch (err) {
        console.error("useUpdateSessionDisplayName onSuccess error:", err);
      }
    },
  });
}
